import { useEffect, useState, useCallback, useRef } from "react";
import { fetchGiveawayWallet, sendGiveaway, fetchGiveawayHistory, searchCoinRecipients } from "@/lib/api";
import { Gift, Send, History } from "lucide-react";
import { toast } from "sonner";

const ic = "w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none";

export default function CreatorGiveaway() {
  const [gw, setGw] = useState(null);
  const [hist, setHist] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const debounce = useRef(null);

  const load = useCallback(() => {
    fetchGiveawayWallet().then(setGw).catch(() => setGw(null));
    fetchGiveawayHistory().then(setHist).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onRecip = (v) => {
    setRecipient(v);
    clearTimeout(debounce.current);
    if (v.trim().length < 2) { setSuggestions([]); return; }
    debounce.current = setTimeout(() => {
      searchCoinRecipients(v).then((r) => setSuggestions(r.results || [])).catch(() => setSuggestions([]));
    }, 250);
  };

  const doSend = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("A reason is required to give DC Coins.");
    setBusy(true);
    try {
      const r = await sendGiveaway({ recipient: recipient.trim(), amount: parseInt(amount, 10), pin, reason: reason.trim() });
      toast.success("Giveaway sent!");
      setGw((w) => ({ ...w, dc_giveaway: r.dc_giveaway }));
      setRecipient(""); setAmount(""); setPin(""); setReason(""); setSuggestions([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send giveaway."); }
    finally { setBusy(false); }
  };

  if (!gw) return null;

  return (
    <div className="glass rounded-2xl p-6 mb-8 border border-[#FFBF00]/25" data-testid="creator-giveaway-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-xl flex items-center gap-2"><Gift size={18} className="text-[#FFBF00]" /> Creator Giveaway Pool</h3>
        <div className="text-sm">Pool balance: <span className="font-display text-2xl text-[#FFBF00]" data-testid="giveaway-balance">{gw.dc_giveaway} DC</span></div>
      </div>
      <p className="text-xs text-white/50 mb-4">Gift these DC Coins to other players — they land in the player's wallet. You cannot use giveaway coins for yourself, and every gift needs a reason.</p>

      {!gw.has_pin && <p className="text-xs text-orange-300 mb-3">Set your Transfer PIN above before you can send giveaways.</p>}

      <form onSubmit={doSend} className="grid sm:grid-cols-2 gap-4">
        <div className="relative">
          <input value={recipient} onChange={(e) => onRecip(e.target.value)} placeholder="Recipient LostDC ID" required data-testid="giveaway-recipient" className={ic} />
          {suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full glass rounded-xl overflow-hidden border border-white/10">
              {suggestions.map((s) => (
                <button type="button" key={s.id} onClick={() => { setRecipient(s.id); setSuggestions([]); }}
                        className="w-full text-left px-4 py-2 hover:bg-[#FFBF00]/15 flex items-center gap-2 text-sm">
                  {s.avatar && <img src={s.avatar} alt="" className="w-6 h-6 rounded-full" />} {s.text}
                </button>
              ))}
            </div>
          )}
        </div>
        <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" required data-testid="giveaway-amount-input" className={ic} />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" required data-testid="giveaway-reason" className={ic} />
        <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Your PIN" required data-testid="giveaway-pin" className={ic} />
        <button type="submit" disabled={busy} data-testid="giveaway-send-btn"
                className="px-6 py-2.5 rounded-xl bg-[#FFBF00] text-black font-display blocky-shadow-black hover-blocky disabled:opacity-50 inline-flex items-center gap-2 justify-center sm:col-span-2">
          <Send size={16} /> Send Giveaway
        </button>
      </form>

      {hist.length > 0 && (
        <div className="mt-6" data-testid="giveaway-history-card">
          <h4 className="font-display text-sm mb-2 flex items-center gap-2 text-white/70"><History size={14} /> Giveaway History</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {hist.map((t) => (
                  <tr key={t.id} className="border-t border-white/5" data-testid={`giveaway-hist-${t.id}`}>
                    <td className="py-2">{t.type === "giveaway_grant" ? "🏦 Received from Admin" : `🎁 To ${t.receiver_lostdc}`}</td>
                    <td className="py-2 text-white/60">{t.note}</td>
                    <td className="py-2 text-right text-[#FFBF00]">{t.amount} DC</td>
                    <td className="py-2 text-right text-white/40">{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
